import {
  SalesTaxSettingsTypes,
  updateCorporationSettings,
  useCorporationSettings,
} from '@commenda/api';
import { SettingsData } from '@commenda/api/new-sales-tax/corporations/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@commenda/kakashi';
import { Country } from '@commenda/prisma';
import { useEffect, useState } from 'react';

interface IndirectTaxFilingSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateSettings: () => void;
  corporationId: number;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const IndirectTaxFilingSettingsModal = ({
  isOpen,
  onClose,
  onUpdateSettings,
  corporationId,
  onSuccess,
  onError,
}: IndirectTaxFilingSettingsModalProps) => {
  const [autoApproval, setAutoApproval] = useState<boolean>(false);
  const [autoApprovalDueDay, setAutoApprovalDueDay] = useState<number>(5);

  const { data: corporationSettings, mutate: refetchCorporationSettings } =
    useCorporationSettings({
      corporationId,
    });

  useEffect(() => {
    if (corporationSettings) {
      const settingsList = corporationSettings.data.filter(
        (setting: SettingsData) =>
          setting.type === SalesTaxSettingsTypes.SALES_TAX_SETTINGS
      );
      if (settingsList.length > 0) {
        const countrywideSetting = settingsList.find(
          (setting: SettingsData) => setting.state === 'COUNTRYWIDE'
        );
        if (countrywideSetting) {
          const approvalConfig =
            countrywideSetting.data.filing_approval_configuration;
          setAutoApproval(approvalConfig?.auto_approval === 'ENABLED');
          setAutoApprovalDueDay(approvalConfig?.auto_approval_due_day ?? 5);
        }
      }
    }
  }, [corporationSettings]);

  const handleSave = async () => {
    try {
      const response = await updateCorporationSettings({
        corporationId,
        settings: {
          country: Country.US,
          type: SalesTaxSettingsTypes.SALES_TAX_SETTINGS,
          configuration: {
            data: {
              filing_approval_configuration: {
                auto_approval: autoApproval ? 'ENABLED' : 'DISABLED',
                auto_approval_due_day: autoApprovalDueDay,
              },
            },
          },
        },
      });

      if (response.status === 201) {
        onSuccess?.('Filing settings have been updated successfully.');
        refetchCorporationSettings();
        onUpdateSettings();
        onClose();
      } else {
        onError?.('Failed to update settings. Please try again.');
      }
    } catch (err) {
      onError?.('Failed to update settings. Please try again.');
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent className="min-w-[600px] p-0" hideCloseIcon>
        <DialogHeader>
          <DialogTitle>Filing settings</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4xl p-3xl">
          <div className="flex justify-between gap-5xl">
            <div className="flex flex-col gap-xs">
              <span className="txt-small-bold text-text-primary">
                Auto approval
              </span>
              <span className="txt-small-regular text-text-tertiary">
                When enabled, filings will be automatically approved based on
                the due day setting.
              </span>
            </div>
            <Switch
              checked={autoApproval}
              onCheckedChange={setAutoApproval}
              size="sm"
            />
          </div>

          <div
            className={`flex flex-col gap-sm ${!autoApproval ? 'opacity-70' : ''}`}
          >
            <div className="txt-small-bold text-text-primary">
              Auto approval due day
            </div>
            <p className="txt-small-regular text-text-tertiary">
              Select the day of the month when filings should be automatically
              approved.
            </p>
            <div className="max-w-[240px]">
              <Select
                value={autoApprovalDueDay.toString()}
                onValueChange={(value) => setAutoApprovalDueDay(+value)}
                disabled={!autoApproval}
              >
                <SelectTrigger className="txt-medium-regular text-text-secondary h-11 px-md">
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 31 }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button onClick={handleSave} size="sm">
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { IndirectTaxFilingSettingsModal };
